import {useEffect, useState} from 'react'
import {CloseButton} from '@headlessui/react'
import {useTranslation} from 'react-i18next'
import {z} from 'zod'
import {twJoin} from 'tailwind-merge'

import {Button, DialogNew} from '@sdox/ui'

import {ErrorMessage} from '../../ErrorMessage'
import {useTipTapEditorContext} from './EditorContext'

import type {Editor} from '@tiptap/react'

const httpsValidation = z.string().url().startsWith('https://')

export interface LinkDialogProps {
  editor: Editor
}

export const LinkDialog = ({editor}: LinkDialogProps) => {
  const {showLinkDialog, toggleLinkDialog} = useTipTapEditorContext()
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | undefined>()
  const {t} = useTranslation()

  useEffect(() => {
    const linkHref = editor.getAttributes('link').href ?? ''
    setUrl(showLinkDialog ? linkHref : '')
    setError(undefined)
  }, [editor, showLinkDialog])

  const unsetLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
      toggleLinkDialog()
    }
  }

  const updateLink = () => {
    const {error} = httpsValidation.safeParse(url)

    if (error) {
      setError(t('common:error.url'))
      return
    }

    try {
      editor
        .chain()
        .focus()
        .setLink({href: url})
        .run()
      toggleLinkDialog()
    } catch (e: any) {
      //  TODO type this error...
      setError(e.error)
    }
  }

  return (
    <DialogNew
      open={showLinkDialog}
      onClose={toggleLinkDialog}
      heading={'Set link'}
      size={'small'}
    >
      <DialogNew.Content>
        <div className={'flex flex-col'}>
          <div className={'flex items-center justify-between font-medium'}>
            <label htmlFor={'editor_url'}>
              {t('common:url')}
            </label>
          </div>
          <input
            id={'editor_url'}
            type={'url'}
            data-autofocus
            tabIndex={0}
            value={url}
            onChange={({currentTarget}) => setUrl(currentTarget.value)}
            placeholder={'https://sdox.io'}
            aria-invalid={Boolean(error)}
            className={twJoin([
              'rounded-radius-interactive-lg border-interactive-outline-default-default',
              'aria-invalid:border-feedback-glyph-semantic-negative aria-invalid:focus:ring-feedback-glyph-semantic-negative',
            ])}
          />
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      </DialogNew.Content>
      <DialogNew.Footer className={'flex w-full justify-between gap-spacing-12'}>
        <CloseButton
          as={Button}
          size={'small'}
          variant={'flat'}
        >
          {t('common:cancel')}
        </CloseButton>
        <div className={'flex gap-spacing-12'}>
          <Button
            size={'small'}
            variant={'filled'}
            className={'self-start'}
            disabled={!editor.getAttributes('link')}
            onClick={unsetLink}
          >
            {t('common:delete')}
          </Button>
          <Button
            size={'small'}
            variant={'filled-brand'}
            onClick={updateLink}
          >
            {t('common:save')}
          </Button>
        </div>
      </DialogNew.Footer>
    </DialogNew>
  )
}
